import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { getMe } from '@/services/auth.service';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Inactivity timeout configuration
  const INACTIVITY_TIMEOUT = 15 * 60 * 1000; // 15 minutes in milliseconds
  const inactivityTimerRef = React.useRef(null);
  const isLoggingOutRef = React.useRef(false);

  useEffect(() => {
    if (token) {
      fetchProfile();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchProfile = async () => {
    try {
      const data = await getMe();
      setUser(data);
    } catch (error) {
      console.error('Error fetching profile:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (token, userData) => {
    isLoggingOutRef.current = false; // Reset logout flag on login
    localStorage.setItem('token', token);
    setToken(token);
    setUser(userData);
  };

  // Clear inactivity timer function
  const clearInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  }, []);

  // Logout function with protection against multiple calls
  const logout = useCallback(() => {
    // Prevent multiple simultaneous logout calls
    if (isLoggingOutRef.current) {
      return;
    }
    isLoggingOutRef.current = true;

    // Clear inactivity timer
    clearInactivityTimer();

    localStorage.removeItem('token');
    setToken(null);
    setUser(null);

    // Reset flag after a short delay
    setTimeout(() => {
      isLoggingOutRef.current = false;
    }, 1000);
  }, [clearInactivityTimer]);

  // Handle inactivity timeout
  const handleInactivityTimeout = useCallback(() => {
    if (isLoggingOutRef.current) {
      return; // Already logging out, skip
    }

    console.log('Timeout por inatividade atingido, fazendo logout automático');
    toast.error('Sessão expirada por inatividade. Faça login novamente.');
    logout();
  }, [logout]);

  // Reset inactivity timer
  const resetInactivityTimer = useCallback(() => {
    // Clear existing timer
    clearInactivityTimer();

    // Only set timer if user is logged in and not logging out
    if (token && user && !isLoggingOutRef.current) {
      inactivityTimerRef.current = setTimeout(handleInactivityTimeout, INACTIVITY_TIMEOUT);
    }
  }, [token, user, INACTIVITY_TIMEOUT, handleInactivityTimeout, clearInactivityTimer]);

  // Setup inactivity monitoring
  useEffect(() => {
    if (!token || !user) {
      clearInactivityTimer();
      return;
    }

    console.log('Configurando monitoramento de inatividade (timeout: 15 minutos)');

    // Events that indicate user activity
    const activityEvents = ['click', 'keypress', 'scroll', 'mousemove', 'touchstart'];

    // Throttle the reset to avoid excessive calls
    let throttleTimeout = null;
    const handleActivity = () => {
      if (throttleTimeout) return;

      throttleTimeout = setTimeout(() => {
        throttleTimeout = null;
      }, 1000); // Throttle to max once per second

      resetInactivityTimer();
    };

    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Start the inactivity timer
    resetInactivityTimer();

    // Cleanup function
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      clearInactivityTimer();
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
    };
  }, [token, user, resetInactivityTimer, clearInactivityTimer]);

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
